import { useState, useEffect } from "react";
import api from "../api";
import Modal from "../components/Modal";

export default function LeaveAdmin() {
  const [tab, setTab] = useState("types");
  const [applyModal, setApplyModal] = useState(false);
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
        <div>
          <h2 className="page-title">Leave Management</h2>
          <p className="page-sub">Leave types, applicability, balances and approvals</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn" onClick={()=>setApplyModal(true)}>+ Apply for Employee</button>
          {tab==="types" && <NewLeaveTypeButton/>}
        </div>
      </div>
      <div className="tabs" style={{marginTop:14}}>
        {[["types","Leave Types"],["requests","Requests"],["balances","Employee Balances"],["holidays","Holidays"]].map(([id,label]) => (
          <button key={id} className={`tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === "types"    && <TypesTab/>}
      {tab === "requests" && <RequestsTab/>}
      {tab === "balances" && <BalancesTab/>}
      {tab === "holidays" && <HolidaysTab/>}

      {applyModal && <ApplyForEmployeeModal onClose={()=>setApplyModal(false)}/>}
    </div>
  );
}

// Shared trigger so TypesTab's internal "New" button lives in the shared header row
let _openNewTypeFn = null;
function NewLeaveTypeButton() {
  return <button className="btn" onClick={()=>_openNewTypeFn && _openNewTypeFn()}>+ New Leave Type</button>;
}

// ── Employee picker (datalist-based, backed by real employee list) ────────
function useEmployeeList() {
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    api.get("/portal/admin/users").then(d => setEmployees(d.users || [])).catch(()=>{});
  }, []);
  return employees;
}

function EmployeePicker({value, onChange, placeholder}) {
  const employees = useEmployeeList();
  const listId = "employee-picker-list";
  return (
    <>
      <input className="inp" list={listId} value={value} placeholder={placeholder || "Type name or ID…"}
        onChange={e=>{
          const typed = e.target.value;
          onChange(typed);
        }}/>
      <datalist id={listId}>
        {employees.map(e => (
          <option key={e.employee_id} value={e.employee_id}>{e.name} — {e.employee_id}</option>
        ))}
      </datalist>
    </>
  );
}

// ── Leave Types ──────────────────────────────────────────────────────────
function TypesTab() {
  const [types, setTypes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const blank = {
    name:"", code:"", color:"#0EA5E9", description:"", is_paid:true,
    days_per_year:12, gender_applicability:"all",
    min_tenure_months:0, allow_during_probation:true,
    min_days_per_application:0.5, max_days_per_application:"", max_consecutive_days:"",
    allow_half_day:true, allow_negative_balance:false, allow_future_leave:true,
    require_attachment:false, attachment_required_above_days:"",
    reason_mandatory:true, allow_cancellation:true, allow_modification:true,
    sandwich_rule_enabled:false, approval_required:true,
  };
  const [form, setForm] = useState(blank);

  const load = () => api.get("/admin/leave_types").then(d => setTypes(d.leave_types||[]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setMsg(""); setModal(true); };
  const openEdit = (t) => { setEditing(t); setForm({...blank, ...t}); setMsg(""); setModal(true); };

  useEffect(() => { _openNewTypeFn = openNew; return () => { _openNewTypeFn = null; }; }, []);

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) { setMsg("Name and code are required."); return; }
    const payload = {...form,
      days_per_year: parseFloat(form.days_per_year)||0,
      min_tenure_months: parseInt(form.min_tenure_months)||0,
      min_days_per_application: parseFloat(form.min_days_per_application)||0.5,
      max_days_per_application: form.max_days_per_application ? parseFloat(form.max_days_per_application) : null,
      max_consecutive_days: form.max_consecutive_days ? parseFloat(form.max_consecutive_days) : null,
      attachment_required_above_days: form.attachment_required_above_days ? parseFloat(form.attachment_required_above_days) : null,
    };
    try {
      if (editing) await api.patch(`/admin/leave_types/${editing.id}`, payload);
      else await api.post("/admin/leave_types", payload);
      setModal(false); load();
    } catch(e) { setMsg(e.message); }
  };

  const disable = async (t) => {
    if (!confirm(`Disable "${t.name}"? Existing requests/balances are kept.`)) return;
    await api.delete(`/admin/leave_types/${t.id}`); load();
  };

  const CB = ({label, field}) => (
    <label style={{display:"flex",gap:6,alignItems:"center",fontWeight:400,fontSize:13}}>
      <input type="checkbox" checked={!!form[field]}
        onChange={e=>setForm({...form,[field]:e.target.checked})}/>
      {label}
    </label>
  );

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12,marginTop:16}}>
        {types.map(t => (
          <div key={t.id} className="card" style={{borderTop:`3px solid ${t.color}`,opacity:t.is_active===false?0.5:1}}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <b>{t.name}</b>
              <span className="pill gray" style={{fontSize:10}}>{t.code}</span>
            </div>
            <div style={{fontSize:12,color:"var(--muted)",margin:"6px 0"}}>{t.description||"No description"}</div>
            <div style={{fontSize:12,display:"grid",gap:2}}>
              <div>Annual: <b>{t.days_per_year}</b> days</div>
              <div>{t.is_paid ? "Paid" : "Unpaid"} · {t.approval_required ? "Approval required" : "Auto-approved"}</div>
              <div>{t.allow_half_day ? "Half-day OK" : "No half-day"} · {t.sandwich_rule_enabled ? "Sandwich rule ON" : "No sandwich rule"}</div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
              <button className="btn small" onClick={()=>openEdit(t)}>Edit</button>
              <button className="btn small danger" onClick={()=>disable(t)}>Disable</button>
              <ApplicabilityLink leaveTypeId={t.id} name={t.name}/>
              <AccrualCarryForwardLink leaveTypeId={t.id} name={t.name}/>
            </div>
          </div>
        ))}
        {!types.length && <div className="card" style={{gridColumn:"1/-1",textAlign:"center",color:"var(--muted)"}}>No leave types yet. Create one to get started.</div>}
      </div>

      {modal && (
        <Modal title={editing ? `Edit ${editing.name}` : "New Leave Type"} onClose={()=>setModal(false)}>
          <div style={{display:"grid",gap:12,maxHeight:"70vh",overflowY:"auto",paddingRight:4}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 100px 60px",gap:10}}>
              <div><label>Name *</label><input className="inp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
              <div><label>Code *</label><input className="inp" value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})}/></div>
              <div><label>Color</label><input className="inp" type="color" value={form.color} onChange={e=>setForm({...form,color:e.target.value})}/></div>
            </div>
            <div><label>Description</label>
              <textarea className="inp" rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><label>Days per Year</label><input className="inp" type="number" step="0.5" value={form.days_per_year} onChange={e=>setForm({...form,days_per_year:e.target.value})}/></div>
              <div><label>Gender Applicability</label>
                <select className="inp" value={form.gender_applicability} onChange={e=>setForm({...form,gender_applicability:e.target.value})}>
                  <option value="all">All</option><option value="male">Male</option><option value="female">Female</option>
                </select></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><label>Min days / application</label><input className="inp" type="number" step="0.5" value={form.min_days_per_application} onChange={e=>setForm({...form,min_days_per_application:e.target.value})}/></div>
              <div><label>Max days / application</label><input className="inp" type="number" step="0.5" value={form.max_days_per_application} onChange={e=>setForm({...form,max_days_per_application:e.target.value})}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><label>Max consecutive days</label><input className="inp" type="number" step="0.5" value={form.max_consecutive_days} onChange={e=>setForm({...form,max_consecutive_days:e.target.value})}/></div>
              <div><label>Min tenure (months)</label><input className="inp" type="number" value={form.min_tenure_months} onChange={e=>setForm({...form,min_tenure_months:e.target.value})}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <CB label="Paid" field="is_paid"/>
              <CB label="Approval required" field="approval_required"/>
              <CB label="Allow half day" field="allow_half_day"/>
              <CB label="Allow negative balance" field="allow_negative_balance"/>
              <CB label="Allow future-dated leave" field="allow_future_leave"/>
              <CB label="Allow during probation" field="allow_during_probation"/>
              <CB label="Reason mandatory" field="reason_mandatory"/>
              <CB label="Allow cancellation" field="allow_cancellation"/>
              <CB label="Allow modification" field="allow_modification"/>
              <CB label="Sandwich rule" field="sandwich_rule_enabled"/>
              <CB label="Require attachment" field="require_attachment"/>
            </div>
            {form.require_attachment && (
              <div><label>Attachment required above (days)</label>
                <input className="inp" type="number" step="0.5" value={form.attachment_required_above_days}
                  onChange={e=>setForm({...form,attachment_required_above_days:e.target.value})}/></div>
            )}
            <button className="btn full" onClick={save}>{editing ? "Save Changes" : "Create Leave Type"}</button>
            {msg && <div style={{color:"var(--danger)",fontSize:13,textAlign:"center"}}>{msg}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Applicability sub-modal ─────────────────────────────────────────────
function ApplicabilityLink({leaveTypeId, name}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [form, setForm] = useState({department_id:"", employee_id:"", is_org_default:false, override_days_per_year:""});

  const load = () => api.get(`/admin/leave_types/${leaveTypeId}/applicability`).then(d=>setItems(d.items||[]));
  useEffect(() => { if (open) { load(); api.get("/portal/departments").then(d=>setDepts(d.departments||[])); } }, [open]);

  const add = async () => {
    const payload = {...form,
      department_id: form.department_id || null,
      employee_id: form.employee_id || null,
      override_days_per_year: form.override_days_per_year ? parseFloat(form.override_days_per_year) : null};
    try { await api.post(`/admin/leave_types/${leaveTypeId}/applicability`, payload); load();
      setForm({department_id:"", employee_id:"", is_org_default:false, override_days_per_year:""});
    } catch(e) { alert(e.message); }
  };
  const remove = async (id) => { await api.delete(`/admin/leave_applicability/${id}`); load(); };

  return (
    <>
      <button className="btn small" onClick={()=>setOpen(true)}>Applicability</button>
      {open && (
        <Modal title={`${name} — Applicability`} onClose={()=>setOpen(false)}>
          <div style={{display:"grid",gap:10}}>
            <div style={{fontSize:12,color:"var(--muted)"}}>Priority: Employee override → Department → Org default.</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <select className="inp" value={form.department_id} onChange={e=>setForm({...form,department_id:e.target.value})}>
                <option value="">— Department (optional) —</option>
                {depts.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <EmployeePicker value={form.employee_id}
                onChange={v=>setForm({...form,employee_id:v})}
                placeholder="Employee (optional)"/>
            </div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <label style={{display:"flex",gap:6,alignItems:"center",fontWeight:400,fontSize:13}}>
                <input type="checkbox" checked={form.is_org_default} onChange={e=>setForm({...form,is_org_default:e.target.checked})}/>
                Org default
              </label>
              <input className="inp" type="number" placeholder="Override days/year (optional)" style={{maxWidth:200}}
                value={form.override_days_per_year} onChange={e=>setForm({...form,override_days_per_year:e.target.value})}/>
              <button className="btn small" onClick={add}>Add</button>
            </div>
            <table className="tbl">
              <thead><tr><th>Scope</th><th>Override Days</th><th></th></tr></thead>
              <tbody>
                {items.map(i=>(
                  <tr key={i.id}>
                    <td>{i.is_org_default ? "Org Default" : i.department ? `Dept: ${i.department}` : `Employee: ${i.employee} (${i.employee_id})`}</td>
                    <td>{i.override_days_per_year ?? "—"}</td>
                    <td><button onClick={()=>remove(i.id)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer"}}>Remove</button></td>
                  </tr>
                ))}
                {!items.length && <tr><td colSpan={3} style={{textAlign:"center",color:"var(--muted)"}}>No applicability rules — leave type uses its default for everyone.</td></tr>}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Accrual + Carry-forward sub-modal ───────────────────────────────────
function AccrualCarryForwardLink({leaveTypeId, name}) {
  const [open, setOpen] = useState(false);
  const [accrual, setAccrual] = useState({frequency:"annual",amount_per_cycle:0,credit_day:1,prorata_enabled:true,prorata_basis:"joining_date",is_automatic:true});
  const [cf, setCf] = useState({mode:"none",max_days:"",percentage:"",expiry_months:"",consumption_order:"carry_first"});

  useEffect(() => {
    if (!open) return;
    api.get(`/admin/leave_types/${leaveTypeId}/accrual`).then(d=>{ if (d.exists) setAccrual(d); });
    api.get(`/admin/leave_types/${leaveTypeId}/carry_forward`).then(d=>{ if (d.exists) setCf(d); });
  }, [open]);

  const saveAccrual = async () => {
    await api.post(`/admin/leave_types/${leaveTypeId}/accrual`, {
      ...accrual, amount_per_cycle: parseFloat(accrual.amount_per_cycle)||0,
      credit_day: parseInt(accrual.credit_day)||1});
    alert("Accrual rule saved.");
  };
  const saveCf = async () => {
    await api.post(`/admin/leave_types/${leaveTypeId}/carry_forward`, {
      ...cf, max_days: cf.max_days || null, percentage: cf.percentage || null,
      expiry_months: cf.expiry_months || null});
    alert("Carry-forward rule saved.");
  };

  return (
    <>
      <button className="btn small" onClick={()=>setOpen(true)}>Accrual / CF</button>
      {open && (
        <Modal title={`${name} — Accrual & Carry Forward`} onClose={()=>setOpen(false)}>
          <div style={{display:"grid",gap:16}}>
            <div>
              <div style={{fontWeight:700,marginBottom:8}}>Accrual</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <select className="inp" value={accrual.frequency} onChange={e=>setAccrual({...accrual,frequency:e.target.value})}>
                  <option value="annual">Annual</option><option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option><option value="half_yearly">Half-Yearly</option>
                </select>
                <input className="inp" type="number" step="0.5" placeholder="Amount per cycle"
                  value={accrual.amount_per_cycle} onChange={e=>setAccrual({...accrual,amount_per_cycle:e.target.value})}/>
              </div>
              <div style={{display:"flex",gap:12,marginTop:8,alignItems:"center"}}>
                <label style={{display:"flex",gap:6,fontWeight:400,fontSize:13}}>
                  <input type="checkbox" checked={accrual.prorata_enabled} onChange={e=>setAccrual({...accrual,prorata_enabled:e.target.checked})}/>Pro-rata
                </label>
                <label style={{display:"flex",gap:6,fontWeight:400,fontSize:13}}>
                  <input type="checkbox" checked={accrual.is_automatic} onChange={e=>setAccrual({...accrual,is_automatic:e.target.checked})}/>Automatic
                </label>
              </div>
              <button className="btn small" style={{marginTop:8}} onClick={saveAccrual}>Save Accrual Rule</button>
            </div>
            <div>
              <div style={{fontWeight:700,marginBottom:8}}>Carry Forward</div>
              <select className="inp" value={cf.mode} onChange={e=>setCf({...cf,mode:e.target.value})}>
                <option value="none">No Carry Forward</option><option value="full">Full</option>
                <option value="limited">Limited (fixed days)</option><option value="percentage">Percentage</option>
              </select>
              {cf.mode==="limited" && <input className="inp" style={{marginTop:8}} type="number" placeholder="Max days"
                value={cf.max_days} onChange={e=>setCf({...cf,max_days:e.target.value})}/>}
              {cf.mode==="percentage" && <input className="inp" style={{marginTop:8}} type="number" placeholder="Percentage"
                value={cf.percentage} onChange={e=>setCf({...cf,percentage:e.target.value})}/>}
              {cf.mode!=="none" && <input className="inp" style={{marginTop:8}} type="number" placeholder="Expiry (months, blank = never)"
                value={cf.expiry_months} onChange={e=>setCf({...cf,expiry_months:e.target.value})}/>}
              <button className="btn small" style={{marginTop:8}} onClick={saveCf}>Save Carry-Forward Rule</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Requests (approvals) ─────────────────────────────────────────────────
function RequestsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get(`/admin/leave_requests?status=${status}`).then(d=>setItems(d.items||[]));
  useEffect(() => { load(); }, [status]);

  const act = async (id, newStatus) => {
    const notes = newStatus === "rejected" ? prompt("Reason for rejection:") : "";
    if (newStatus === "rejected" && !notes) return;
    try { await api.patch(`/admin/leave_requests/${id}`, {status:newStatus, notes: notes||""}); load(); }
    catch(e) { alert(e.message); }
  };

  return (
    <div>
      <div style={{display:"flex",gap:8,margin:"12px 0"}}>
        {["pending","approved","rejected"].map(s=>(
          <button key={s} className={`tab ${status===s?"active":""}`} onClick={()=>setStatus(s)}>{s}</button>
        ))}
      </div>
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Applied</th>{status==="pending"&&<th></th>}</tr></thead>
          <tbody>
            {items.map(r=>(
              <tr key={r.id}>
                <td><b>{r.employee_name}</b><div style={{fontSize:11,color:"var(--muted)"}}>{r.employee}</div></td>
                <td>{r.leave_type}</td><td>{r.from_date}</td><td>{r.to_date}</td>
                <td>{r.days}{r.is_half_day && " (half)"}</td>
                <td style={{maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.reason||"—"}</td>
                <td style={{whiteSpace:"nowrap",fontSize:12,color:"var(--muted)"}}>{r.applied_on}</td>
                {status==="pending" && <td style={{display:"flex",gap:6}}>
                  <button className="btn small" onClick={()=>act(r.id,"approved")}>Approve</button>
                  <button className="btn small danger" onClick={()=>act(r.id,"rejected")}>Reject</button>
                </td>}
              </tr>
            ))}
            {!items.length && <tr><td colSpan={8} style={{textAlign:"center",color:"var(--muted)"}}>No {status} requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Apply for Employee (admin on-behalf) ────────────────────────────────
function ApplyForEmployeeModal({onClose}) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({employee_id:"", leave_type_id:"", from_date:"", to_date:"", is_half_day:false, half_type:"first", reason:"", auto_approve:false});
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/leave_types").then(d=>setTypes(d.leave_types||[])); }, []);

  const submit = async () => {
    if (!form.employee_id || !form.leave_type_id || !form.from_date || !form.to_date) {
      setMsg("Employee, leave type and dates are required."); return;
    }
    try {
      const res = await api.post("/admin/leave_requests/apply_for_employee", {
        ...form, leave_type_id: parseInt(form.leave_type_id),
        half_type: form.is_half_day ? form.half_type : null,
      });
      setMsg(`✓ Applied — ${res.days} day(s) ${form.auto_approve ? "(auto-approved)" : "(pending approval)"}`);
      setTimeout(onClose, 1500);
    } catch(e) { setMsg(e.message); }
  };

  return (
    <Modal title="Apply Leave for Employee" onClose={onClose}>
      <div style={{display:"grid",gap:12}}>
        <div><label>Employee *</label>
          <EmployeePicker value={form.employee_id} onChange={v=>setForm({...form,employee_id:v})} placeholder="Employee ID or name"/>
        </div>
        <div><label>Leave Type *</label>
          <select className="inp" value={form.leave_type_id} onChange={e=>setForm({...form,leave_type_id:e.target.value})}>
            <option value="">— Select —</option>
            {types.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div><label>From *</label><input className="inp" type="date" value={form.from_date} onChange={e=>setForm({...form,from_date:e.target.value})}/></div>
          <div><label>To *</label><input className="inp" type="date" value={form.to_date} onChange={e=>setForm({...form,to_date:e.target.value})}/></div>
        </div>
        {form.from_date && form.from_date === form.to_date && (
          <div style={{display:"flex",gap:14,alignItems:"center"}}>
            <label style={{display:"flex",gap:6,alignItems:"center",fontWeight:400}}>
              <input type="checkbox" checked={form.is_half_day} onChange={e=>setForm({...form,is_half_day:e.target.checked})}/>Half Day
            </label>
            {form.is_half_day && (
              <select className="inp" style={{width:"auto"}} value={form.half_type} onChange={e=>setForm({...form,half_type:e.target.value})}>
                <option value="first">First Half</option><option value="second">Second Half</option>
              </select>
            )}
          </div>
        )}
        <div><label>Reason</label>
          <textarea className="inp" rows={2} value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})}/></div>
        <label style={{display:"flex",gap:6,alignItems:"center",fontWeight:400}}>
          <input type="checkbox" checked={form.auto_approve} onChange={e=>setForm({...form,auto_approve:e.target.checked})}/>
          Auto-approve immediately (skip pending state)
        </label>
        <button className="btn full" onClick={submit}>Submit</button>
        {msg && <div style={{textAlign:"center",fontSize:13,color:msg.startsWith("✓")?"var(--ok)":"var(--danger)"}}>{msg}</div>}
      </div>
    </Modal>
  );
}

// ── Employee balances + manual adjustment ────────────────────────────────
function BalancesTab() {
  const [empCode, setEmpCode] = useState("");
  const [balances, setBalances] = useState([]);
  const [types, setTypes] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [adjustForm, setAdjustForm] = useState({leave_type_id:"", adjustment_type:"credit", quantity:"", reason:""});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get("/admin/leave_types").then(d=>setTypes(d.leave_types||[])); }, []);

  const [searched, setSearched] = useState(false);
  const [activeEmp, setActiveEmp] = useState("");
  const search = () => {
    const code = empCode.split("—").pop().trim(); // in case they picked "Name — ID" from datalist
    if (!code) return;
    setNotFound(false);
    setSearched(false);
    api.get(`/admin/leave_balances/${code}`)
      .then(d=>{ setBalances(d.balances||[]); setSearched(true); setActiveEmp(code); })
      .catch(()=>{ setBalances([]); setNotFound(true); setSearched(false); });
  };
  const loadLedger = () => {
    const code = empCode.split("—").pop().trim();
    api.get(`/admin/leave_ledger/${code}`).then(d=>{setLedger(d.items||[]); setShowLedger(true);});
  };

  const adjust = async () => {
    const code = empCode.split("—").pop().trim();
    if (!adjustForm.leave_type_id || !adjustForm.quantity || !adjustForm.reason.trim()) {
      alert("Leave type, quantity and reason are required."); return;
    }
    try {
      await api.post(`/admin/leave_balances/${code}/adjust`, {
        ...adjustForm, leave_type_id: parseInt(adjustForm.leave_type_id), quantity: parseFloat(adjustForm.quantity)});
      search();
      setAdjustForm({leave_type_id:"", adjustment_type:"credit", quantity:"", reason:""});
    } catch(e) { alert(e.message); }
  };

  return (
    <div>
      <div style={{display:"flex",gap:8,margin:"12px 0",alignItems:"flex-start"}}>
        <div style={{flex:1,maxWidth:400}}>
          <EmployeePicker value={empCode} onChange={setEmpCode} placeholder="Type employee name or ID…"/>
        </div>
        <button className="btn" onClick={search}>Search</button>
        {balances.length>0 && <button className="btn small" onClick={loadLedger}>View Ledger</button>}
      </div>
      {notFound && <div style={{color:"var(--danger)",fontSize:13,marginBottom:12}}>Employee not found. Pick a name from the suggestions list.</div>}

      {balances.length>0 && (
        <div className="card" style={{marginBottom:16}}>
          <table className="tbl">
            <thead><tr><th>Leave Type</th><th>Allocated</th><th>Carried Fwd</th><th>Used</th><th>Available</th></tr></thead>
            <tbody>
              {balances.map(b=>(
                <tr key={b.id}><td>{b.leave_type}</td><td>{b.allocated}</td><td>{b.carried_forward}</td><td>{b.used}</td><td><b>{b.available}</b></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!searched && !notFound && (
        <div className="card" style={{textAlign:"center",color:"var(--muted)"}}>
          Pick an employee above (start typing a name or ID) and click Search.
        </div>
      )}

      {searched && balances.length===0 && (
        <div className="card" style={{textAlign:"center",color:"var(--muted)",marginBottom:16}}>
          {activeEmp} has no leave balances yet. Use the form below to allocate their first balance
          (e.g. Credit 12 days of Earned Leave).
        </div>
      )}

      {searched && (
        <div className="card">
          <div style={{fontWeight:700,marginBottom:10}}>Manual Balance Adjustment</div>
          <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr 2fr auto",gap:8,alignItems:"end"}}>
            <div><label>Leave Type</label>
              <select className="inp" value={adjustForm.leave_type_id} onChange={e=>setAdjustForm({...adjustForm,leave_type_id:e.target.value})}>
                <option value="">—</option>
                {types.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
            <div><label>Type</label>
              <select className="inp" value={adjustForm.adjustment_type} onChange={e=>setAdjustForm({...adjustForm,adjustment_type:e.target.value})}>
                <option value="credit">Credit</option><option value="debit">Debit</option><option value="set_balance">Set Balance</option>
              </select></div>
            <div><label>Quantity</label>
              <input className="inp" type="number" step="0.5" value={adjustForm.quantity} onChange={e=>setAdjustForm({...adjustForm,quantity:e.target.value})}/></div>
            <div><label>Reason *</label>
              <input className="inp" value={adjustForm.reason} onChange={e=>setAdjustForm({...adjustForm,reason:e.target.value})}/></div>
            <button className="btn" onClick={adjust}>Apply</button>
          </div>
        </div>
      )}

      {showLedger && (
        <Modal title={`Ledger — ${empCode}`} onClose={()=>setShowLedger(false)}>
          <table className="tbl">
            <thead><tr><th>Date</th><th>Type</th><th>Txn</th><th>Qty</th><th>Balance After</th><th>By</th><th>Reason</th></tr></thead>
            <tbody>
              {ledger.map(t=>(
                <tr key={t.id}>
                  <td style={{whiteSpace:"nowrap",fontSize:12}}>{t.created_at}</td>
                  <td>{t.leave_type}</td>
                  <td><span className="pill blue" style={{fontSize:10}}>{t.txn_type.replace("_"," ")}</span></td>
                  <td style={{color:t.quantity<0?"#ef4444":"#16a34a",fontWeight:600}}>{t.quantity>0?"+":""}{t.quantity}</td>
                  <td>{t.balance_after}</td>
                  <td style={{fontSize:11,color:"var(--muted)"}}>{t.created_by}</td>
                  <td style={{fontSize:12,color:"var(--muted)"}}>{t.reason}</td>
                </tr>
              ))}
              {!ledger.length && <tr><td colSpan={7} style={{textAlign:"center",color:"var(--muted)"}}>No transactions.</td></tr>}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}

// ── Holidays ──────────────────────────────────────────────────────────────
function HolidaysTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({date:"", name:"", holiday_type:"national", remarks:""});
  const now = new Date();
  const load = () => api.get(`/admin/holidays?year=${now.getFullYear()}`).then(d=>setItems(d.items||[]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.date || !form.name.trim()) { alert("Date and name required."); return; }
    try { await api.post("/admin/holidays", form); load(); setForm({date:"",name:"",holiday_type:"national",remarks:""}); }
    catch(e) { alert(e.message); }
  };
  const remove = async (id) => { if(!confirm("Remove this holiday?")) return; await api.delete(`/admin/holidays/${id}`); load(); };

  return (
    <div>
      <div className="card" style={{margin:"12px 0"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1.5fr 1fr 1.5fr auto",gap:8,alignItems:"end"}}>
          <div><label>Date</label><input className="inp" type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/></div>
          <div><label>Name</label><input className="inp" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
          <div><label>Type</label>
            <select className="inp" value={form.holiday_type} onChange={e=>setForm({...form,holiday_type:e.target.value})}>
              <option value="national">National</option><option value="optional">Optional</option><option value="restricted">Restricted</option>
            </select></div>
          <div><label>Remarks</label><input className="inp" value={form.remarks} onChange={e=>setForm({...form,remarks:e.target.value})}/></div>
          <button className="btn" onClick={add}>Add</button>
        </div>
      </div>
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Date</th><th>Name</th><th>Type</th><th>Remarks</th><th></th></tr></thead>
          <tbody>
            {items.map(h=>(
              <tr key={h.id}><td>{h.date}</td><td>{h.name}</td><td><span className="pill blue">{h.holiday_type}</span></td>
                <td style={{color:"var(--muted)"}}>{h.remarks||"—"}</td>
                <td><button onClick={()=>remove(h.id)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer"}}>Remove</button></td></tr>
            ))}
            {!items.length && <tr><td colSpan={5} style={{textAlign:"center",color:"var(--muted)"}}>No holidays yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
